import React from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import I18n from 'i18n-js';
import { ddRum, rumErrorCtx, SEVERITY } from '@web-platform/browser-rum';
import { isDev, isProduction } from 'common/utils/environment-utils';
import Storage from 'grab-storage';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isTomorrow from 'dayjs/plugin/isTomorrow';

import Store from './store';
import App from './App';
import './index.less';
// eslint-disable-next-line
import 'grab-ui/dist/theme.less';
import { APP_BASE_URL_NAME } from './constants/paths';
import { getLocale } from './common/utils/locales';

dayjs.extend(isToday);
dayjs.extend(isTomorrow);

window.Grab = {
  config: {},
  router: {},
  storage: Storage,
};

const renderApp = () => {
  render(
    <Provider store={Store}>
      <BrowserRouter basename={`/${APP_BASE_URL_NAME}`}>
        <App />
      </BrowserRouter>
    </Provider>,
    document.getElementById('root'),
  );
};

window
  .fetch(require('config.json'))
  .then(async configResponse => {
    if (configResponse.status !== 200) return;
    window.Grab.config = await configResponse.json();
    const locale = getLocale();
    window
      .fetch(require(`public/locales/${locale}.json`))
      .then(async localeResponse => {
        if (localeResponse.status !== 200) return;
        I18n.translations[locale] = await localeResponse.json();
        I18n.locale = locale;

        // Initialize browser-rum with selective imports and buildDefaultConfig
        if (!isDev()) {
          const rumConfig = ddRum.buildDefaultConfig({
            applicationId: 'f88ecc62-18a6-4f99-b1bb-cd70d0e0872b',
            clientToken: 'pub24e2fa2f730fd447da86a4d8a6214aa5',
            service: window.Grab.config.appId.toLowerCase(),
            env: window.Grab.config.env,
            version: process.env.APP_VERSION,
            beforeSend: (event, context) => {
              if (event.type === 'error' && event.error) {
                const errorPatternsToIgnore = [
                  /zaloJSV2/i,                        // Zalo webview bridge
                  /WebViewJavascriptBridge/i,         // Shopee webview bridge
                  /Script error\./i,                  // Generic cross-origin script errors
                  /Failed to fetch/i,                 // Failed to fetch
                  /AbortError/i,                      // AbortError
                  /Request aborted/i,                 // Request aborted
                  /NetworkError/i,                    // Network Error
                  /Network Error/i,                   // Network Error
                  /The operation was aborted/i,       // The operation was aborted
                  /TypeError: Load failed/i,          // Load failed
                  /timeout exceeded/i,                // Timeout exceeded
                  /Failed to initialize WebGL/i,
                  /service-worker\.js load failed/i,
                  /Failed to register a ServiceWorker/i,
                  // map-related errors
                  /Init complex text layout shaper failed/i,  
                  /glyphMap/i,                                
                  /Large Render Blocking Asset/i,             
                  /Failed to execute 'getImageData' on 'CanvasRenderingContext2D'/i, 
                  /Invalid LngLat object:/i,
                  /Ignored attempt to cancel a touchmove event/i,
                ];
  
                const isIgnoreError = errorPatternsToIgnore.some(pattern => pattern.test(event.error.message));

                if (isIgnoreError) {
                  // Log ignored errors in non-production environments for debugging
                  if (!isProduction()) {
                    console.log('[RUM] Ignored error:', event, context);
                  }
                  return false; // Return false to discard the event
                }
              }

              return event;
            },
          });

          if (isProduction()) {
            rumConfig.sessionSampleRate = 1;
          }

          ddRum.init(rumConfig);
        }
        
        renderApp();
      })
      .catch(err => {
        console.error(err);
        // Error tracking with new browser-rum API using selective imports
        const errorCtx = rumErrorCtx()
          .component('index')
          .message('Failed to initialize application')
          .severity(SEVERITY.CRITICAL)
          .custom('locale', locale)
          .build();
        ddRum.addError(err, errorCtx);
      });
  })
  .catch(err => {
    console.error(err);
    // Error tracking with new browser-rum API using selective imports
    const errorCtx = rumErrorCtx()
      .component('window.fetch.catch')
      .message('Failed to load config')
      .severity(SEVERITY.CRITICAL)
      .build();
    ddRum.addError(err, errorCtx);
  });

// Global error handler using selective imports
window.addEventListener('error', (event) => {
  const errorCtx = rumErrorCtx()
    .component('window.addEventListener.error')
    .message(event.message || 'Unhandled error')
    .severity(SEVERITY.CRITICAL)
    .build();
  
  // Pass event.error (the actual Error object) if available
  ddRum.addError(event.error || event, errorCtx);
});

// Unhandled promise rejection handler using selective imports
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const isError = reason instanceof Error;
  const errorMessage = isError 
    ? reason.message 
    : (typeof reason === 'string' ? reason : 'Unhandled promise rejection');
  
  const errorCtx = rumErrorCtx()
    .component('window.addEventListener.unhandledrejection')
    .message(errorMessage)
    .severity(SEVERITY.HIGH)
    .build();
  
  // Pass event.reason (the actual error) instead of event
  ddRum.addError(reason || new Error(errorMessage), errorCtx);
});

if (module.hot) {
  module.hot.accept('./App', () => {
    renderApp();
  });
}
